type State = {
  keys: Set<string>; // sensitive keys, lowercased
  done: WeakMap<object, unknown>; // finished copies (shared refs reuse these)
  inProgress: Set<object>; // ancestors on the current path (cycles)
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function hasToJSON(value: object): value is { toJSON: () => unknown } {
  return typeof (value as { toJSON?: unknown }).toJSON === 'function';
}

function copyKeys(source: Record<string, unknown>, target: Record<string, unknown>, state: State): void {
  for (const key of Object.keys(source)) {
    if (state.keys.has(key.toLowerCase())) continue;
    target[key] = sanitize(source[key], state);
  }
}

function sanitize(value: unknown, state: State): unknown {
  if (typeof value !== 'object' || value === null) return value;
  if (state.inProgress.has(value)) return '[Circular]';
  if (state.done.has(value)) return state.done.get(value);

  state.inProgress.add(value);
  let result: unknown;
  try {
    if (hasToJSON(value)) {
      // What JSON.stringify would use: axios 0.21 errors, AxiosError, AxiosHeaders, Date
      result = sanitize(value.toJSON(), state);
    } else if (Array.isArray(value)) {
      const copy: unknown[] = [];
      for (let i = 0; i < value.length; i++) copy[i] = sanitize(value[i], state);
      result = copy;
    } else if (value instanceof Error) {
      const copy: Record<string, unknown> = { name: value.name, message: value.message, stack: value.stack };
      copyKeys(value as unknown as Record<string, unknown>, copy, state);
      result = copy;
    } else if (isPlainObject(value)) {
      const copy: Record<string, unknown> = {};
      copyKeys(value, copy, state);
      result = copy;
    } else {
      result = value; // XMLHttpRequest, DOM nodes, Map/Set, other class instances
    }
  } finally {
    state.inProgress.delete(value);
  }
  state.done.set(value, result);
  return result;
}

/**
 * Returns a deep copy of `args` with every key in `sensitiveKeys` removed
 * (case-insensitive, at any depth). The original args are never modified.
 */
export function sanitizeArgs(args: unknown[], sensitiveKeys: string[]): unknown[] {
  try {
    const state: State = {
      keys: new Set(sensitiveKeys.map((k) => k.toLowerCase())),
      done: new WeakMap(),
      inProgress: new Set(),
    };
    return args.map((arg) => sanitize(arg, state));
  } catch {
    // Never fall back to raw args, since that could leak sensitive data
    return ['[log args could not be sanitized]'];
  }
}
